import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loading from '@/app/component/guard/loading'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { checkCampaignForValidationErrors } from '@/app/module/campaigns/campaign-validation-helpers'
import { NO_SENDER_ID_TEXT, type SenderIdsForDripType } from '@/app/module/campaigns/helpers'
import { useAbortCampaign } from '@/app/module/campaigns/hooks/use-abort-campaign'
import { useCopyCampaign } from '@/app/module/campaigns/hooks/use-copy-campaign'
import { useExportCampaignReport } from '@/app/module/campaigns/hooks/use-export-campaign-report'
import { useGetCampaign } from '@/app/module/campaigns/hooks/use-get-campaign'
import { useGetCampaignSummary } from '@/app/module/campaigns/hooks/use-get-campaign-summary'
import { usePersonalizationList } from '@/app/module/campaigns/hooks/use-personalization-list'
import { useSetCampaignArchived } from '@/app/module/campaigns/hooks/use-set-campaign-archived'
import { useSetCampaignOpen } from '@/app/module/campaigns/hooks/use-set-campaign-open'
import { useSetCampaignPublished } from '@/app/module/campaigns/hooks/use-set-campaign-published'
import { useUnsubscribeAllContacts } from '@/app/module/campaigns/hooks/use-unsubscribe-all-contacts'
import { useUpdateCampaign } from '@/app/module/campaigns/hooks/use-update-campaign'
import { useOrgSwitchGuard } from '@/app/module/campaigns/utils/campaign'
import { useGetSenderIds } from '@/app/module/common/hooks/use-get-sender-ids'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId, selectUserTimezone } from '@/app/module/user/store/selectors'
import {
  selectCountriesTimezones,
  selectCountriesTimezonesByCountry,
  selectDefaultCountryCode,
} from '@/app/module/utils/store/selectors'
import { useAppDispatch } from '@/app/store/redux-hooks'

import { CampaignItem, CampaignSummary, CampaignTemplateType } from '../../../types'
import { getAbortConfirmation, getPublishConfirmation, getUnsubAllConfirmation } from '../../confirmations'
import DownloadReportDialog from '../../list/download-report'
import Dashboard from './dashboard'
import { type CampaignMode, type DashboardCampaign, type DashboardData, type DashboardTab } from './types'

type Props = {
  itemId: string
  tabName: DashboardTab
}

const emptySenderIdsForDrip: SenderIdsForDripType = {
  sms: [],
  voice: [],
  whatsapp: [],
}

export const ERROR_COPY_CAMPAIGN_ID_TEXT = 'Failed to copy campaign ID. Please copy it manually.'
const UNKNOWN_VALIDATION_ERROR_TEXT = 'Campaign settings are incomplete. Review the campaign editor before going live.'

const CampaignDashboard: React.FC<Props> = ({ itemId, tabName }) => {
  const createConfirmation = useConfirmation()
  const countriesTimezones = useSelector(selectCountriesTimezones)
  const countryCode = useSelector(selectDefaultCountryCode)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const orgId = useSelector(selectOrgId)
  const personalizationList = usePersonalizationList()
  const { data: senderIdsForDrip } = useGetSenderIds()
  const timezone = useSelector(selectUserTimezone)
  const timezones = useSelector(selectCountriesTimezonesByCountry)
  const { data: item, isFetching, isLoading, refetch: refetchCampaign } = useGetCampaign(itemId)
  const {
    data: itemSummary,
    isFetching: isSummaryFetching,
    isLoading: isSummaryLoading,
    refetch: refetchCampaignSummary,
  } = useGetCampaignSummary(item?.launched ? itemId : undefined)
  const [abortCampaign, abortCampaignRes] = useAbortCampaign()
  const [copyCampaign, copyCampaignRes] = useCopyCampaign()
  const [exportCampaignReport] = useExportCampaignReport()
  const [setCampaignArchived, setCampaignArchivedRes] = useSetCampaignArchived()
  const [setCampaignOpen, setCampaignOpenRes] = useSetCampaignOpen()
  const [setCampaignPublished, setCampaignPublishedRes] = useSetCampaignPublished()
  const [unsubscribeAllContacts, unsubscribeAllContactsRes] = useUnsubscribeAllContacts()
  const [updateCampaign] = useUpdateCampaign()
  const [downloadReportOpen, setDownloadReportOpen] = React.useState(false)
  const activeTab = tabName
  const campaignId = Number(itemId)
  const campaignSummary = itemSummary as CampaignSummary | undefined
  const showLoading =
    isLoading || (isFetching && !item) || Boolean(item?.launched && (isSummaryLoading || isSummaryFetching))
  const shouldRedirectStoppedSubscriptions = activeTab === 'subscriptions' && Boolean(item?.aborted)

  useOrgSwitchGuard(orgId)

  React.useEffect(() => {
    if (shouldRedirectStoppedSubscriptions) {
      navigate(`/campaigns/${itemId}`, { replace: true })
    }
  }, [itemId, shouldRedirectStoppedSubscriptions, navigate])

  const dashboardData = React.useMemo<DashboardData>(
    () => ({
      errors: item ? getDashboardValidationErrors(item, senderIdsForDrip) : [],
    }),
    [item, senderIdsForDrip],
  )

  const handleAbortCampaign = React.useCallback(async () => {
    if (
      !item ||
      item.aborted ||
      !item.launched ||
      !Number.isInteger(campaignId) ||
      campaignId <= 0 ||
      abortCampaignRes.isLoading
    ) {
      return
    }

    const confirmed = await createConfirmation(getAbortConfirmation(item.name))

    if (confirmed) {
      await abortCampaign(campaignId)
    }
  }, [abortCampaignRes.isLoading, campaignId, item, abortCampaign, createConfirmation])

  const handleCampaignNameSubmit = React.useCallback(
    async (name: string) => {
      const updatedCampaign = await updateCampaign(itemId, { name })
      if (!updatedCampaign) {
        throw new Error('Campaign name update failed')
      }
    },
    [itemId, updateCampaign],
  )

  const handleCampaignIdCopied = React.useCallback(() => {
    dispatch(
      createNotification({
        'copy-id': {
          type: 'success',
          message: 'Campaign ID copied!',
        },
      }),
    )
  }, [dispatch])

  const handleCampaignIdCopyFailed = React.useCallback(() => {
    dispatch(
      createNotification({
        'copy-id': {
          type: 'error',
          message: ERROR_COPY_CAMPAIGN_ID_TEXT,
        },
      }),
    )
  }, [dispatch])

  const handleEditCampaign = React.useCallback(() => {
    if (item?.aborted) {
      return
    }

    navigate(`/campaigns/edit/${itemId}/content`)
  }, [item?.aborted, itemId, navigate])

  const handleBack = React.useCallback(() => {
    navigate('/campaigns')
  }, [navigate])

  const handleOverview = React.useCallback(() => {
    navigate(`/campaigns/${itemId}`)
  }, [itemId, navigate])

  const handleAnalytics = React.useCallback(() => {
    navigate(`/campaigns/${itemId}/analytics`)
  }, [itemId, navigate])

  const handleSubscriptions = React.useCallback(() => {
    navigate(`/campaigns/${itemId}/subscriptions`)
  }, [itemId, navigate])

  const handlePublishCampaign = React.useCallback(async () => {
    const confirmed = await createConfirmation(getPublishConfirmation())

    if (!confirmed) {
      return
    }

    await setCampaignPublished({
      campaignId,
      published: true,
    })
  }, [campaignId, createConfirmation, setCampaignPublished])

  const handleRefreshOverview = React.useCallback(async () => {
    if (item?.launched) {
      await Promise.all([refetchCampaign(), refetchCampaignSummary()])
      return
    }

    await refetchCampaign()
  }, [item?.launched, refetchCampaign, refetchCampaignSummary])

  const handleReturnToDraft = React.useCallback(async () => {
    await setCampaignPublished({
      campaignId,
      published: false,
    })
  }, [campaignId, setCampaignPublished])

  const handleSetCampaignOpen = React.useCallback(async () => {
    if (
      !item ||
      item.aborted ||
      !item.launched ||
      !Number.isInteger(campaignId) ||
      campaignId <= 0 ||
      setCampaignOpenRes.isLoading
    ) {
      return
    }

    await setCampaignOpen({
      campaignId,
      open: !item.open,
    })
  }, [campaignId, item, setCampaignOpenRes.isLoading, setCampaignOpen])

  const handleArchiveCampaign = React.useCallback(async () => {
    await setCampaignArchived({
      campaignId,
      archived: !item?.archived,
    })
  }, [campaignId, item?.archived, setCampaignArchived])

  const handleDuplicateCampaign = React.useCallback(async () => {
    const response = await copyCampaign(campaignId)
    if (response) {
      navigate(`/campaigns/edit/${response.id}/content`)
    }
  }, [campaignId, copyCampaign, navigate])

  const handleDownloadReport = React.useCallback(() => {
    if (item?.launched && !isFetching) {
      setDownloadReportOpen(true)
    }
  }, [isFetching, item?.launched])

  const handleDownloadReportClose = React.useCallback(() => {
    setDownloadReportOpen(false)
  }, [])

  const handleDownloadReportConfirm = React.useCallback(
    ({ timezone: reportTimezone, fields, labels }: { timezone: string; fields: string[]; labels: boolean }) => {
      if (!Number.isInteger(campaignId) || campaignId <= 0) {
        return
      }

      setDownloadReportOpen(false)
      exportCampaignReport({
        campaignId,
        fields,
        labels,
        timezone: reportTimezone,
      })
    },
    [campaignId, exportCampaignReport],
  )

  const handleUnsubscribeAllContacts = React.useCallback(async () => {
    if (!item || !Number.isInteger(campaignId) || campaignId <= 0) {
      return
    }

    const confirmed = await createConfirmation(getUnsubAllConfirmation(item.name))

    if (confirmed) {
      await unsubscribeAllContacts(campaignId)
    }
  }, [campaignId, item, createConfirmation, unsubscribeAllContacts])

  return (
    <Loading isLoading={showLoading}>
      {item && downloadReportOpen && (
        <DownloadReportDialog
          campaignName={item.name}
          countriesTimezones={countriesTimezones}
          isOpen
          personalizationList={personalizationList}
          timezone={timezone}
          timezones={timezones}
          onClose={handleDownloadReportClose}
          onConfirm={handleDownloadReportConfirm}
        />
      )}
      {item && !shouldRedirectStoppedSubscriptions ? (
        <Dashboard
          activeTab={activeTab}
          actionLoading={{
            abortCampaign: abortCampaignRes.isLoading,
            archiveCampaign: setCampaignArchivedRes.isLoading,
            campaign: isFetching,
            duplicateCampaign: copyCampaignRes.isLoading,
            publishCampaign: setCampaignPublishedRes.isLoading,
            setCampaignOpen: setCampaignOpenRes.isLoading,
            unsubscribeAllContacts: unsubscribeAllContactsRes.isLoading,
          }}
          campaign={mapCampaignToDashboardCampaign(item, itemId)}
          campaignItem={item}
          campaignSummary={campaignSummary}
          countriesTimezones={countriesTimezones}
          countryCode={countryCode}
          data={dashboardData}
          itemId={campaignId}
          orgId={orgId}
          refreshing={isFetching || isSummaryFetching}
          timezone={timezone}
          timezones={timezones}
          onAbortCampaign={handleAbortCampaign}
          onAnalytics={handleAnalytics}
          onArchiveCampaign={handleArchiveCampaign}
          onBack={handleBack}
          onCampaignIdCopyFailed={handleCampaignIdCopyFailed}
          onCampaignIdCopied={handleCampaignIdCopied}
          onCampaignNameSubmit={handleCampaignNameSubmit}
          onDownloadReport={handleDownloadReport}
          onDuplicateCampaign={handleDuplicateCampaign}
          onEditCampaign={handleEditCampaign}
          onOverview={handleOverview}
          onPublishCampaign={handlePublishCampaign}
          onRefresh={handleRefreshOverview}
          onReturnToDraft={handleReturnToDraft}
          onSetCampaignOpen={handleSetCampaignOpen}
          onSubscriptions={handleSubscriptions}
          onUnsubscribeAllContacts={handleUnsubscribeAllContacts}
        />
      ) : null}
    </Loading>
  )
}

type ValidatableCampaign = NonNullable<Parameters<typeof checkCampaignForValidationErrors>[0]['item']>

function getDashboardValidationErrors(campaign: CampaignItem, senderIdsForDrip?: SenderIdsForDripType): string[] {
  const validation = isValidatableCampaign(campaign)
    ? checkCampaignForValidationErrors({
        item: campaign,
        senderIdsForDrip: senderIdsForDrip || emptySenderIdsForDrip,
      })
    : {
        errorMessages: [],
        isValid: true,
      }
  const errors = senderIdsForDrip
    ? [...validation.errorMessages]
    : validation.errorMessages.filter((errorMessage) => errorMessage !== NO_SENDER_ID_TEXT)
  const hasOnlyPendingSenderIdError = !senderIdsForDrip && validation.errorMessages.length > 0 && errors.length === 0

  if (!validation.isValid && !errors.length && !hasOnlyPendingSenderIdError) {
    errors.push(UNKNOWN_VALIDATION_ERROR_TEXT)
  }

  return Array.from(new Set(errors))
}

function isValidatableCampaign(campaign: CampaignItem): campaign is ValidatableCampaign {
  return campaign.type !== CampaignTemplateType.Chatbot
}

export function resolveCampaignMode(campaign?: Partial<Pick<CampaignItem, 'launched' | 'published'>>): CampaignMode {
  if (campaign?.published && campaign.launched) {
    return 'live'
  }

  if (campaign?.published && !campaign.launched) {
    return 'scheduled'
  }

  return 'draft'
}

export function mapCampaignToDashboardCampaign(campaign: CampaignItem, campaignId: string): DashboardCampaign {
  return {
    aborted: Boolean(campaign.aborted),
    archived: Boolean(campaign.archived),
    id: campaignId,
    launchOpen: campaign.launchOpen ?? true,
    launched: Boolean(campaign.launched),
    name: campaign.name || 'Untitled campaign',
    open: Boolean(campaign.open),
    published: Boolean(campaign.published),
    runs: campaign.runs || 0,
    runsDone: campaign.runsDone || 0,
    runsInProgress: campaign.runsInProgress || 0,
    status: resolveCampaignMode(campaign),
    type: campaign.type,
  }
}

export default CampaignDashboard
